import { escapeHtml } from "./simpleHtml";
import { fillSignatureHtml, SIGNATURE_PLACEHOLDER } from "./signatureFill";
import { plainReadingOf } from "./plainReading";

/** The mail signature (the top bar, signature and reports round, 2026-10-26; brief docs/briefs/top-bar-signature-reports-round.md, rule 3;
 *  DECISIONS "2026-10-26 — The top bar, signature and reports round…"). One copy of the rules for every reader: the sender, the two
 *  mock-ups in Settings, the composer's "Added when sent", the reply sample and the connector's get_instructions.
 *  - The workspace holds the signature (`organization.signature`, the founder's text as its column default); a project may switch to its
 *    own (`projects.own_signature` + `projects.signature`). Empty means no signature.
 *  - Placeholders are filled in one pass at send time; unknown braces stay as typed (the saved replies' `{first}` is the customer's name
 *    there, not the sender's). A line that held a placeholder and came out empty is dropped; a line the owner left empty stays. */

/** the founder's text: new and existing workspaces start with it (the column's default) */
export const DEFAULT_SIGNATURE = "{firstname} {lastname}\n{project} Support Team\n{logo}";
/** 4,000 characters since the signature HTML and role round (2026-10-28; 500 before): a table is wordy */
export const SIGNATURE_MAX = 4000;
/** the editor's chips, in this order (the connector tags round, 2026-10-27: `{role}`, the member's customer-facing role) */
export const SIGNATURE_PLACEHOLDERS = ["{firstname}", "{lastname}", "{name}", "{role}", "{project}", "{workspace}", "{logo}"] as const;
/** the custom sender line's chips (the same round): the signature's without the logo, since a mail's From line shows no picture */
export const SENDER_LINE_PLACEHOLDERS = SIGNATURE_PLACEHOLDERS.filter((p) => p !== "{logo}");
/** the logo's default width in the mail, in pixels; `{logo W}` and `{logo WxH}` set another size within these bounds */
export const SIGNATURE_LOGO_WIDTH = 100;
export const SIGNATURE_LOGO_BOUNDS = Object.freeze({ width: [5, 600], height: [5, 400] } as const); // 5 px since 2026-10-28 (was 24 wide, 16 high)

export type SignatureLogo = { url: string; alt: string };
export type SignatureVars = {
  /** the sending person's customer-facing name (null: nobody, as for an automatic reply) */
  name: string | null;
  project: string;
  workspace: string;
  /** the chosen logo's picture: in a mail "cid:<id>", the inline attachment's own id; on the screens a same-host path; null: none */
  logo: SignatureLogo | null;
  /** the sending person's customer-facing role in this workspace (the connector tags round; null or empty: the line goes) */
  role?: string | null;
  /** the signature's two colours (the signature HTML and role round, 2026-10-28): the HTML is wrapped in the text colour and every link carries
   *  the link colour; null or absent: no colours (the text part never has any) */
  colors?: SignatureColors | null;
};
export type SignatureColors = { text: string; link: string };
export type SignatureFormat = "text" | "html";

/** the text that applies: the project's own while it switched to it, else the workspace's (either may be empty: no signature) */
export function signatureTemplate(o: { own: boolean; project: string; workspace: string }): string {
  return o.own ? o.project : o.workspace;
}

/** the name the signature uses for a person: the customer-facing name, else the team name, else the account's name (today's rule) */
export function signatureName(o: { customerName?: string | null; displayName?: string | null; name?: string | null } | null): string | null {
  if (!o) return null;
  return o.customerName?.trim() || o.displayName?.trim() || o.name?.trim() || null;
}

/** the logo for a signature on the screens (the composer's "Added when sent"): the chosen logo's path after the app's address. An SVG gives
 *  one too since the mail logo round (2026-10-27): the mail carries an SVG logo as its PNG copy, by cid (the sender's own address, never this) */
export function signatureLogo(path: string | null | undefined, appUrl: string, alt: string): SignatureLogo | null {
  if (!path) return null;
  return { url: `${appUrl.replace(/\/$/, "")}${path}`, alt };
}

/** the seven values, as text (the logo apart) */
function valuesOf(vars: SignatureVars): Record<string, string> {
  const words = (vars.name ?? "").trim().split(/\s+/).filter(Boolean);
  return { firstname: words[0] ?? "", lastname: words.slice(1).join(" "), name: words.join(" "), role: (vars.role ?? "").trim(), project: vars.project, workspace: vars.workspace };
}

export type LogoSize = { width: number; height: number | null };
const within = (n: number, [lo, hi]: readonly [number, number]) => n >= lo && n <= hi;
/** the size a logo placeholder asks for: its width (and height) when inside the bounds, else the default 100 px wide with an automatic height */
export function logoSizeOf(width: string | undefined, height: string | undefined): LogoSize {
  const w = width === undefined ? NaN : Number(width), h = height === undefined ? null : Number(height);
  const ok = within(w, SIGNATURE_LOGO_BOUNDS.width) && (h === null || within(h, SIGNATURE_LOGO_BOUNDS.height));
  return ok ? { width: w, height: h } : { width: SIGNATURE_LOGO_WIDTH, height: null };
}
const logoHtml = (logo: SignatureLogo, size: LogoSize) =>
  `<img src="${escapeHtml(logo.url)}" width="${size.width}"${size.height === null ? "" : ` height="${size.height}"`} alt="${escapeHtml(logo.alt)}" style="display:block;width:${size.width}px;height:${size.height === null ? "auto" : `${size.height}px`};border:0">`;

const isColor = (v: string | null | undefined): v is string => typeof v === "string" && /^#[0-9a-f]{6}$/i.test(v);

/** The signature filled in for one mail part (2026-10-28: always read as simple HTML, the short list applied at every fill). HTML: the kept
 *  tags, every text and value escaped, the logo's picture, inside `<div style="color:…">` when colours are given. Plain text: the plain
 *  reading of that HTML without a logo (plainReading.ts). Empty or blank: "" (no signature). */
export function fillSignature(template: string, vars: SignatureVars, format: SignatureFormat): string {
  const values = valuesOf(vars);
  if (format === "text") return plainReadingOf(fillSignatureHtml(template, { values, logo: null, linkColor: null }));
  const logo = vars.logo ? { html: (w: string | undefined, h: string | undefined) => logoHtml(vars.logo!, logoSizeOf(w, h)) } : null;
  const colors = vars.colors && isColor(vars.colors.text) && isColor(vars.colors.link) ? vars.colors : null;
  const html = fillSignatureHtml(template, { values, logo, linkColor: colors?.link ?? null });
  if (!html.trim()) return "";
  return colors ? `<div style="color:${colors.text}">${html}</div>` : html;
}

/** The old line-by-line filling, as plain text, for the custom sender line (a From line is no HTML: "Kriesi <support>" stays as typed). A
 *  line that held a placeholder and came out empty is dropped; trailing spaces go; the logo is nothing. */
export function fillPlainPlaceholders(template: string, vars: Omit<SignatureVars, "logo" | "colors">): string {
  const values = valuesOf({ ...vars, logo: null });
  const lines = template.replace(/\r\n?/g, "\n").split("\n").map((line) => {
    let held = false;
    const out = line.replace(SIGNATURE_PLACEHOLDER, (_m, key: string | undefined) => { held = true; return key === undefined ? "" : values[key]!; });
    return { out: out.replace(/\s+$/, ""), held };
  }).filter((l) => !(l.held && l.out.trim() === "")).map((l) => l.out);
  while (lines.length && !lines[0]!.trim()) lines.shift();
  while (lines.length && !lines[lines.length - 1]!.trim()) lines.pop();
  return lines.join("\n");
}
